import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { MaImage } from '@/lib/zybooks-api';
import authService from '@/services/zybook-auth-service';
import { Clipboard, Download, EllipsisVertical, Pencil, Text, Trash } from 'lucide-react';
import { getMaImageUrl } from './image-library-utils';

interface ImageOptionsDropdownProps {
    image: MaImage;
    onEditAltText: (image: MaImage) => void;
    onEdit?: (image: MaImage) => void;
    onDelete?: (image: MaImage) => void;
    onOpenChange?: (open: boolean) => void;
}

export const ImageOptionsDropdown: React.FC<ImageOptionsDropdownProps> = props => {
    const { image, onEdit, onEditAltText, onDelete, onOpenChange } = props;

    const downloadImage = async (image: MaImage) => {
        const imageUrl = getMaImageUrl(image);
        try {
            const response = await fetch(imageUrl, { mode: 'cors' });
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = image.filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download image:', error);
        }
    };

    return (
        <DropdownMenu onOpenChange={onOpenChange}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="p-1 rounded hover:text-black cursor-pointer"
                    onClick={e => e.stopPropagation()}
                >
                    <EllipsisVertical className="w-5 h-5" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                onClick={e => e.stopPropagation()}
            >
                <DropdownMenuGroup>
                    <DropdownMenuItem
                        onClick={() => {
                            navigator.clipboard.writeText(`${getMaImageUrl(image)}`);
                        }}
                    >
                        <Clipboard className="mr-2 w-4 h-4" />
                        Copy Image URL
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => {
                            navigator.clipboard.writeText(image.image_uuid.toString());
                        }}
                    >
                        <Clipboard className="mr-2 w-4 h-4" />
                        Copy Image ID
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => downloadImage(image)}>
                        <Download className="mr-2 w-4 h-4" />
                        Download Image
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onEditAltText(image)}>
                        <Text className="mr-2 w-4 h-4" />
                        Edit alt text
                    </DropdownMenuItem>
                    {authService.isSuper() && onEdit && onDelete && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => onEdit(image)}>
                                <Pencil className="mr-2 w-4 h-4" />
                                Edit Image Name
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => onDelete(image)}>
                                <Trash className="mr-2 w-4 h-4 text-red-600" />
                                <span className="text-red-600">Delete Image</span>
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
